import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { isClubless } from '../auth/postLoginRoute'
import { RankingSection } from './data/RankingCard'

/**
 * `/sin-club` — la única pantalla de una cuenta que no pertenece a ningún
 * club (decisión de mg, 2026-09-09: "si hace sign in y no está en un club,
 * entonces no debería poder hacer nada").
 *
 * Es el estado NORMAL de cualquiera que se registra solo: desde esa fecha el
 * alta pública deja `player_profile.club_id` en NULL en vez de meterlo en el
 * club default, y el backend responde 403 `club_required` a `POST /sessions`
 * (ver `backend/app/services/default_club.py`). Hasta que el staff de su club
 * lo dé de alta con este mail, no hay panel, no hay juego y no hay
 * cuestionarios — el alta por club es también lo que le crea el cronograma
 * (`backend/app/services/study_schedule.py`).
 *
 * Lo único que queda a mano es el RANKING ("el ranking nomás podríamos dejar
 * público", mg): la misma sección que ve un jugador en `/data`. Una cuenta sin
 * club sólo alcanza el pool sin club, nunca a nadie de un club — el scope lo
 * resuelve el backend (`app/routers/ranking.py::resolve_ranking_scope`).
 *
 * El mail va bien visible y copiable porque ES el dato que el staff necesita:
 * el alta del jugador se hace por mail (`POST /club/{id}/players`), así que
 * pedirlo "con este mail" es literalmente el paso siguiente.
 */
export function NoClubPage() {
  const { user, logout, refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  // Con club (o staff, o superadmin) esta pantalla no aplica: al `/` y que
  // `postLoginRoute` / `PlayBridge` decidan. Cubre el caso de volver acá por
  // bookmark después de que el club te dio de alta.
  if (user && !isClubless(user)) return <Navigate to="/" replace />

  const email = user?.email || user?.username || ''

  async function recheck() {
    setBusy(true)
    try {
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
    } catch {
      // Sin permiso de portapapeles el mail igual está a la vista y se
      // selecciona a mano: no es un error que valga la pena mostrar.
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-lg px-lg py-2xl">
      <div className="rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]">
        <h1>Tu cuenta no está asignada a ningún club</h1>
        <p className="m-0 text-sm leading-relaxed text-text-secondary">
          VerSports se usa a través de un club. Pedile al staff de tu club que
          te dé de alta como jugador con este mail y vas a poder entrenar.
        </p>
      </div>

      <div className="card flex flex-col gap-md border-primary/30">
        <div className="flex flex-col gap-xs">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-text-muted">
            Tu mail
          </span>
          <div className="flex flex-wrap items-center gap-sm">
            <strong className="break-all text-base">{email}</strong>
            {email && (
              <button type="button" className="btn-ghost" onClick={() => void copyEmail()}>
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            )}
          </div>
        </div>
        <p className="m-0 text-sm leading-relaxed text-text-secondary">
          Tiene que ser exactamente este mail: el club te da de alta por mail, y
          con el alta te llegan también tus cuestionarios del estudio.
        </p>
        <div className="flex flex-wrap gap-sm">
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => void recheck()}
          >
            {busy ? 'Verificando…' : 'Ya me dieron de alta'}
          </button>
          <button type="button" className="btn-ghost" onClick={() => void logout()}>
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* Lo único que sigue a mano sin club. */}
      <RankingSection userId={user?.user_id} />
    </div>
  )
}
